import React from "react";

import BaseCheckbox from "../Checkboxes/BaseCheckbox";
import BaseRadioButton from "../RadioButtons/BaseRadioButton";

import styles from "./GroupList.module.scss";

type GroupListFieldType = "checkbox" | "radio";

interface GroupListItem {
    /**
     * An otional list of nested items
     */
    items?: GroupListItem[];
    /**
     * List item label
     */
    label: string;
    /**
     * List item sub label
     */
    subLabel?: string;
    /**
     * Value of radio button or checkbox
     */
    value?: string;
    /**
     * Checked status of radio button or checkbox
     */
    defaultChecked?: boolean;
    /**
     * Value of vendor category ID (optional)
     */
    vendorCategoryID?: number;
    /**
     * Value of vendor term ID (optional)
     */
    vendorTermID?: number;
}

interface GroupListProps {
    /**
     * List of GroupListItem objects
     */
    items: GroupListItem[];
    /**
     * Title of the group list
     */
    title: string,
    /**
     * Input type
     */
    fieldType: GroupListFieldType;
    /**
     * Optional onChange callBack for checkboxes
     */
    onCheckboxChange?: (listItem: GroupListItem) => void;
}

interface GroupedItemsProps extends GroupListProps {
    tooManyRadioButtonsChecked: boolean;
    checkedItems: GroupListItem[];
}

interface GroupItemElementProps extends GroupListItem {
    checkboxState?: { [k: string]: boolean;};
    label: string;
    fieldType: GroupListFieldType;
    onCheckboxChange?: (listItem: GroupListItem) => void;
}

const GroupItemElement = (props: GroupItemElementProps) => {
    const {
        label = "",
        subLabel,
        value,
        defaultChecked,
        fieldType,
        onCheckboxChange
    } = props;

    // UseCallBack instead of arrow functions when used as a prop.
    const handleCheckboxChange = React.useCallback(() => {
        onCheckboxChange?.({
            label,
            subLabel,
            value,
            defaultChecked,
            items: undefined
        });
    }, [defaultChecked, label, onCheckboxChange, subLabel, value]);


    const labelText = subLabel ? `${label} (${subLabel})` : label;
    const Component = fieldType === "radio" ? BaseRadioButton : BaseCheckbox;
    const componentProps: React.ComponentProps<typeof Component> = {
        checked: false,
        inputId: `id-${value}`,
        labelText,
        inputProps: {
            value,
            checked: defaultChecked
        },

        classes: {
            checkMark: styles.checkmark,
            container: styles.itemContainer,
            inputContainer: styles.inputContainer,
            label: styles.labelText
        }
    };

    return <Component onChange={handleCheckboxChange} {...componentProps} />;
};

/**
 * Creates a list of grouped items
 */
const GroupedItems = ({
    items,
    title,
    tooManyRadioButtonsChecked,
    checkedItems,
    fieldType,
    onCheckboxChange
}: GroupedItemsProps) => (
    <>
        {items.map((item) => {
            const isNested = "items" in items[0] && items[0]?.items?.length;

            if (isNested) {
                const groupedItems = item.items as GroupListItem[];
                let checkedItems: GroupListItem[] = [];
                if (fieldType === "radio") {
                    checkedItems = groupedItems.filter(
                        ({ defaultChecked }:{defaultChecked?: boolean}) => defaultChecked === true
                    );
                }
                const checkedItemsLength = checkedItems.length;
                const tooManyRadioButtonsChecked = fieldType === "radio" && checkedItemsLength > 1;
                return (
                    <div className={styles.group} key={item.label.split(" ").join("_").toLowerCase()}>
                        <h4 className={styles.groupName}>{item.label}</h4>
                        <GroupedItems
                            items={groupedItems}
                            title={title}
                            tooManyRadioButtonsChecked={tooManyRadioButtonsChecked}
                            checkedItems={checkedItems}
                            fieldType={fieldType}
                            onCheckboxChange={onCheckboxChange}
                        />
                    </div>
                );
            }

            const overrideProps: { [key: string]: any } = {};

            if (tooManyRadioButtonsChecked) {
                const lastCheckedItem = checkedItems[checkedItems.length - 1];
                overrideProps.defaultChecked = item.value === lastCheckedItem.value;
            }
            const groupItemElementProps = {
                ...item,
                defaultChecked: item.defaultChecked,
                fieldType,
                onCheckboxChange,
                ...overrideProps
            };
            return <GroupItemElement key={`${title}-${item.value}`} {...groupItemElementProps} />;
        })}
    </>
);

/**
 * Renders a grouped list of checkboxes or radio buttons.
 */
const GroupList = (props: GroupListProps) => {
    const {
        items,
        title,
        fieldType,
        onCheckboxChange,
    } = props;

    const groupedItems = items as GroupListItem[];
    let checkedItems: GroupListItem[] = [];
    if (fieldType === "radio") {
        checkedItems = groupedItems.filter(({ defaultChecked }:{defaultChecked?: boolean}) => defaultChecked === true);
    }
    const checkedItemsLength = checkedItems.length;
    const tooManyRadioButtonsChecked = fieldType === "radio" && checkedItemsLength > 1;
    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{title}</h3>
            <div className={styles.items}>
                <GroupedItems
                    items={groupedItems}
                    title={title}
                    tooManyRadioButtonsChecked={tooManyRadioButtonsChecked}
                    checkedItems={checkedItems}
                    fieldType={fieldType}
                    onCheckboxChange={onCheckboxChange}
                />
            </div>
        </div>
    );
};

export { GroupList as default };
export type {
    GroupListProps, GroupListFieldType, GroupListItem
};
